import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dropinApi } from "@bw/shared/api";
import type { CourtAssignmentResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Join a court on the self-serve board (POST
 * /facilities/{id}/drop-in/courts/{courtId}/join → CourtAssignmentResponse | null).
 *
 * Used both to START a game on an `open` court and to JOIN an `in_game` court
 * with open slots. The backend validates: a solo player cannot claim an empty
 * court, so an `open`-court join with no party surfaces a 409
 * (SoloCannotStartCourtError) on `mutation.error` — the page maps it to honest
 * "need a party / join a game" copy (never a raw error). The mutation does NOT
 * swallow errors.
 *
 * On settlement the board, caller entry, and party inventory re-read server
 * truth, including after stale rejection — never an optimistic local flip.
 */
export function useJoinCourt(facilityId: string) {
  const queryClient = useQueryClient();
  return useMutation<CourtAssignmentResponse | null, unknown, string>({
    mutationFn: (courtId: string) => dropinApi.joinCourt(facilityId, courtId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.courtBoard(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: ["dropin", "my-entry", facilityId],
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
